/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.balanceChangedHandler) {
        window.removeEventListener('sm-club:balance-changed', this.balanceChangedHandler);
      }

      clearTimeout(this.slideTimer);
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      this.startSlider();
      this.initClubCashSlide().catch(() => {});

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    startSlider(reset = true) {
      if (!this.slider) return;

      const getSlides = () => Array.from(
        this.slider.querySelectorAll('.announcement__text:not([hidden])')
      );

      const initialSlides = getSlides();
      if (!initialSlides.length) return;

      if (reset) {
        initialSlides.forEach((slide) => slide.classList.remove('is-visible'));
        initialSlides[0].classList.add('is-visible');
      } else {
        const current = this.slider.querySelector('.announcement__text.is-visible:not([hidden])');
        if (!current || !initialSlides.includes(current)) {
          initialSlides.forEach((slide) => slide.classList.remove('is-visible'));
          initialSlides[0].classList.add('is-visible');
        }
      }

      const nextSlide = () => {
        const slides = getSlides();
        if (!slides.length) return;

        if (!this.isPaused && slides.length > 1) {
          let currSlide = this.slider.querySelector('.announcement__text.is-visible:not([hidden])');

          if (!currSlide || !slides.includes(currSlide)) {
            currSlide = slides[0];
            currSlide.classList.add('is-visible');
          }

          const currSlideIndex = slides.indexOf(currSlide);
          const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;
          const next = slides[nextSlideIndex];

          if (document.dir === 'rtl') {
            this.slider.scrollTo(-Math.abs(nextSlideIndex * next.clientWidth), 0);
          } else {
            this.slider.scrollTo(next.offsetLeft - 32, 0);
          }

          currSlide.classList.remove('is-visible');
          next.classList.add('is-visible');
        }

        this.slideTimer = setTimeout(nextSlide, Number(this.dataset.slideDelay) || 7000);
      };

      clearTimeout(this.slideTimer);
      this.slideTimer = setTimeout(nextSlide, Number(this.dataset.slideDelay) || 7000);
    }

    showClubCashNow() {
      const slide = this.querySelector('[data-sm-club-cash-slide]:not([hidden])');
      if (!slide || !this.slider) return;

      const current = this.slider.querySelector('.announcement__text.is-visible');
      if (current && current !== slide) current.classList.remove('is-visible');

      slide.classList.add('is-visible');

      if (document.dir === 'rtl') {
        this.slider.scrollTo(-Math.abs(slide.offsetLeft), 0);
      } else {
        this.slider.scrollTo(slide.offsetLeft - 32, 0);
      }

      this.startSlider(false);
    }

    async initClubCashSlide(force = false) {
      const slide = this.querySelector('[data-sm-club-cash-slide]');
      if (!slide) return;

      const cacheKey = 'sm-club:header-balance:v1';
      const cacheTtl = 3 * 60 * 1000;
      let availablePesewas = null;

      try {
        if (force) sessionStorage.removeItem(cacheKey);

        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - Number(parsed.cached_at || 0) < cacheTtl) {
            availablePesewas = Math.max(0, Number(parsed.available_pesewas || 0));
          } else {
            sessionStorage.removeItem(cacheKey);
          }
        }

        if (availablePesewas === null) {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 1800);

          try {
            const response = await fetch('/apps/sm-club/header-balance', {
              credentials: 'same-origin',
              cache: 'no-store',
              headers: { Accept: 'application/json' },
              signal: controller.signal,
            });

            if (!response.ok) throw new Error('SM Club balance unavailable');
            const payload = await response.json();
            availablePesewas = Math.max(0, Number(payload?.available_pesewas || 0));
          } finally {
            clearTimeout(timeout);
          }

          sessionStorage.setItem(cacheKey, JSON.stringify({
            available_pesewas: availablePesewas,
            cached_at: Date.now(),
          }));
        }

        if (availablePesewas <= 0) {
          slide.hidden = true;
          slide.classList.remove('is-visible');
          return;
        }

        const copy = slide.querySelector('[data-sm-club-cash-copy]');
        if (!copy) return;

        copy.textContent = `Your GH₵${(availablePesewas / 100).toFixed(2)} Club Cash is ready to use on your next order.`;
        slide.hidden = false;
        this.showClubCashNow();

        if (!this.balanceChangedHandler) {
          this.balanceChangedHandler = async () => {
            sessionStorage.removeItem(cacheKey);
            await this.initClubCashSlide(true);
          };
          window.addEventListener('sm-club:balance-changed', this.balanceChangedHandler);
        }
      } catch (error) {
        slide.hidden = true;
        slide.classList.remove('is-visible');
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
